import { Component, ElementRef, ViewChild, OnInit, AfterViewInit, OnDestroy } from '@angular/core';
import { DialogAddSiteComponent } from '@app/old-ui/dialog/dialog-add-site/dialog-add-site.component';
import { DialogService } from '@app/old-ui/dialog/dialog.service';
import { User$, WebClient } from '@injectables';
import { vehiclesDefaultColors } from "src/colors";
import { Observable, Subject, combineLatest } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { geojson } from '@lib/geojson'
import { VehicleQueryService } from 'src/injectables/vehicle-query.service';
import { DialogSetDeviceLocationComponent } from '@app/old-ui/dialog/dialog-set-device-location/dialog-set-device-location.component';
import { Device, VehicleLocationQuery, Vehicle } from '@models';
import { Color } from '@lib/color';
import { op } from '@lib/rxjs';
import { MatDrawer } from '@angular/material/sidenav';
import { MatSelectionListChange } from '@angular/material/list';
import { LocationMapService } from './location-map.service';
import { PolylineType } from '@app/utilities/map/features/polyline';
import { FragQueryService } from 'src/injectables/frag-query.service';
import { DateFormatConfig } from '@app/utilities/date-format-config';
import { LocationPlaybackService } from './location-playback.service';
import { LocationTelemetryService } from './location-telemetry.service';

@Component({
	selector: 'location',
	templateUrl: './location.component.html',
	styleUrls: ['./location.component.scss']
})
export class LocationComponent implements OnInit, AfterViewInit, OnDestroy {  // Implement OnDestroy
	constructor(
		private readonly vehicleQuery: VehicleQueryService,
		public readonly user$: User$,
		private readonly dialog: DialogService,
		private readonly webClient: WebClient,
		public readonly fragQuery: FragQueryService,
		public readonly locationMapService: LocationMapService,
		public readonly dateFormat: DateFormatConfig,
		public readonly playback: LocationPlaybackService,
		private readonly telemetry: LocationTelemetryService,
	) { }

	@ViewChild('map', { static: false }) mapElement: ElementRef;

	private readonly addSiteDialog = DialogAddSiteComponent;
	private readonly setDevceLocation = DialogSetDeviceLocationComponent;
	public readonly device$ = this.vehicleQuery.devices$;
	public readonly devices$ = this.vehicleQuery.devices$;
	public readonly sites$ = this.vehicleQuery.sites$;
	public readonly duration$ = this.vehicleQuery.duration$;
	public readonly durationOptions$ = this.vehicleQuery.durationOptions;
	public readonly geofenceRecords$ = this.vehicleQuery.geofenceRecords$;
	public readonly vehicleTraceTelemetryEnabled$ = combineLatest([
		this.fragQuery.clients$,
		this.fragQuery.sites$,
		this.fragQuery.siteId$,
		this.fragQuery.client$,
	]).pipe(
		op.map(([clients, sites, selectedSiteId, fallbackClient]) => {
			const selectedSite = selectedSiteId == null
				? null
				: sites.find((site) => site.id === selectedSiteId);

			const selectedClient = selectedSite
				? clients.find((client) => client.id === selectedSite.clientId)
				: fallbackClient;

			return isFeatureFlagEnabled(selectedClient?.featureFlags, 'vehicleTraceTelemetry');
		})
	);
	public readonly dateTimeFormat = this.dateFormat.getFormat()
	public drawingManagerOption: DrawingManagerOption;
	public disableAddDevice: Boolean = true;
	public unassignedDevices: Device[] = [];
	public VehiclesPathLocations: VehicleLocationQuery.Interval.VehicleLocationsWithPathColor[];
	private readonly colors = vehiclesDefaultColors;
	public vehicleProperties: [Vehicle, Color, boolean][] = [];
	public readonly deviceClient = this.webClient.model.device;
	public vehicleListDrawerOpen = false;
	public geofenceRecordDrawerOpen = false;
	private mapInitialized = false; // Track map initialization
	private vehicleTracePaths: PolylineType[] = [];
	public playbackPanelOpen = false;
	public readonly expandedVehicleIds = new Set<number>();
	public readonly trigger$ = this.fragQuery.trigger$;
	private readonly destroy$ = new Subject<void>();

	@ViewChild('drawer') drawer: MatDrawer;
	@ViewChild('geofenceDrawer') geofenceDrawer: MatDrawer;

	ngOnInit() {
		this.vehicleQuery.duration$
			.pipe(takeUntil(this.destroy$))
			.subscribe((duration) => this.playback.setDurationKey(duration));

		this.vehicleQuery.vehicleLocationForInterval$
			.pipe(takeUntil(this.destroy$))
			.subscribe((response) => this.playback.updateTimeBounds(response));

		this.locationMapService.vehicleListDrawerToggle$
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.toggleDrawers('vehiclelist');
			});

		this.locationMapService.geofenceRecordDrawerToggle$
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => {
				this.toggleDrawers('geofenceRecord');
			});

		this.locationMapService.playbackPanelToggle$
			.pipe(takeUntil(this.destroy$))
			.subscribe(() => this.togglePlaybackPanel());

		this.devices$
			.pipe(takeUntil(this.destroy$))
			.subscribe(devices => {
				devices.forEach(device => {
					if (!device.geometry) {
						this.unassignedDevices.push(device)
						this.disableAddDevice = false;
					}
				})
			})

		this.vehicleQuery.vehicles$
			.pipe(takeUntil(this.destroy$))
			.subscribe(vehicles => {
				const selectedStateByVehicleId = new Map(
					this.vehicleProperties.map(([vehicle, , isSelected]) => [vehicle.id, isSelected])
				);

				this.vehicleProperties = (vehicles ?? []).map((vehicle, index) => [
					vehicle,
					this.colors[index % this.colors.length],
					selectedStateByVehicleId.get(vehicle.id) ?? true,
				]);
				this.locationMapService.vehicleProperties$.next(this.vehicleProperties);
			});

		this.vehicleLocations$
			.pipe(takeUntil(this.destroy$))
			.subscribe(locations => {
				if (!locations) {
					return;
				}

				this.VehiclesPathLocations = locations;
				this.playback.setVehicles(this.playbackVehicles);
				this.updatePath();
				if (this.playbackMode && !this.playbackHasLocations) {
					this.goLive();
				}
			})
	}

	public toggleDrawers(drawerType: 'vehiclelist' | 'geofenceRecord') {
		if (drawerType === 'vehiclelist') {
			if (this.geofenceDrawer.opened) {
				this.geofenceDrawer.close().then(() => {
					this.drawer.toggle();
					this.vehicleListDrawerOpen = this.drawer.opened;
				});
			} else {
				this.drawer.toggle();
				this.vehicleListDrawerOpen = this.drawer.opened;
			}
			this.geofenceRecordDrawerOpen = false;
		} else if (drawerType === 'geofenceRecord') {
			if (this.drawer.opened) {
				this.drawer.close().then(() => {
					this.geofenceDrawer.toggle();
					this.geofenceRecordDrawerOpen = this.geofenceDrawer.opened;
				});
			} else {
				this.geofenceDrawer.toggle();
				this.geofenceRecordDrawerOpen = this.geofenceDrawer.opened;
			}
			this.vehicleListDrawerOpen = false;
		}
	}

	updatePath() {
		if (this.playbackMode) {
			this.playback.updatePaths();
			return;
		}
		this.processVehiclesPathLocations();
		this.locationMapService.addTracePaths(this.vehicleTracePaths);
	}

	ngAfterViewInit() {
		if (!this.mapInitialized) {
			setTimeout(() => {
				this.locationMapService.initializeMap([0, 0], this.mapElement);
				this.mapInitialized = true;
			}, 100);
		}
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
		this.playback.destroy();
		this.locationMapService.cleanup();
		this.mapInitialized = false;
	}

	public toggleDrawer() {
		this.drawer.toggle();
	}

	public toggleVehicleVisibility(vehicleId: number): void {
		this.vehicleProperties = this.vehicleProperties.map(([vehicle, color, isSelected]) => [
			vehicle,
			color,
			vehicle.id === vehicleId ? !isSelected : isSelected,
		]);

		this.locationMapService.vehicleProperties$.next(this.vehicleProperties);
		this.playback.setVehicles(this.playbackVehicles);
		if (this.playbackMode) {
			this.playback.refresh();
		} else {
			this.updatePath();
		}
	}

	public onVehicleSelectionChange(event: MatSelectionListChange): void {
		const selectedVehicleIds = new Set(
			event.source.selectedOptions.selected.map((option) => option.value?.id)
		);

		this.vehicleProperties = this.vehicleProperties.map(([vehicle, color]) => [
			vehicle,
			color,
			selectedVehicleIds.has(vehicle.id),
		]);

		this.locationMapService.vehicleProperties$.next(this.vehicleProperties);
		this.playback.setVehicles(this.playbackVehicles);
		if (this.playbackMode) {
			this.playback.refresh();
		} else {
			this.updatePath();
		}
	}

	public get playbackVehicles(): VehicleLocationQuery.Interval.VehicleLocationsWithPathColor[] {
		const selectedVehicleIds = new Set(
			this.vehicleProperties
				.filter(([, , isSelected]) => isSelected)
				.map(([vehicle]) => vehicle.id)
		);
		const hasSelectionState = this.vehicleProperties.length > 0;

		return (this.VehiclesPathLocations ?? []).filter((vehicle) =>
			vehicle.vehicleLocations.length > 0
			&& (!hasSelectionState || selectedVehicleIds.has(vehicle.vehicleId))
		);
	}

	public get playbackMode(): boolean {
		return this.playback.mode;
	}

	public get playbackPlaying(): boolean {
		return this.playback.playing;
	}

	public get playbackProgress(): number {
		return this.playback.progress;
	}

	public get playbackSpeed(): number {
		return this.playback.speed;
	}

	public get playbackDurationKey(): string {
		return this.playback.durationKey;
	}

	public get playbackHasLocations(): boolean {
		return this.playback.hasLocations();
	}

	public selectPlaybackDuration(duration: string): void {
		this.goLive();
		this.playback.setDurationKey(duration);
		this.playback.clearTimeBounds();
		this.vehicleQuery.duration$.next(duration);
	}

	public togglePlaybackPanel(): void {
		this.playbackPanelOpen = !this.playbackPanelOpen;
	}

	public closePlaybackPanel(): void {
		this.playbackPanelOpen = false;
		if (this.playbackMode) this.goLive();
	}

	public startPlayback(): void {
		this.playback.start();
	}

	public togglePlayback(): void {
		this.playback.toggle();
	}

	public playPlayback(): void {
		this.playback.play();
	}

	public pausePlayback(): void {
		this.playback.pause();
	}

	public seekPlayback(progress: string): void {
		this.playback.seek(progress);
	}

	public setPlaybackSpeed(speed: string | number): void {
		this.playback.setSpeed(speed);
	}

	public goLive(): void {
		this.playback.goLive();
		this.updatePath();
	}

	public formatPlaybackTime(): string {
		return this.playback.formatTime();
	}

	public toggleVehicleDetails(vehicleId: number): void {
		if (this.expandedVehicleIds.has(vehicleId)) {
			this.expandedVehicleIds.delete(vehicleId);
			return;
		}

		this.expandedVehicleIds.add(vehicleId);
	}

	public isVehicleExpanded(vehicleId: number): boolean {
		return this.expandedVehicleIds.has(vehicleId);
	}

	public getVehicleTelemetry(vehicleId: number): VehicleLocationQuery.Interval.VehicleLocationsWithPathColor | undefined {
		return this.telemetry.getVehicleTelemetry(vehicleId, this.VehiclesPathLocations);
	}

	public get selectedVehicleCount(): number {
		return this.vehicleProperties.filter(([, , isSelected]) => isSelected).length;
	}

	public get fleetAverageSpeedMph(): number {
		return this.telemetry.getFleetAverageSpeedMph(this.VehiclesPathLocations);
	}

	public get totalIdleTimeSeconds(): number {
		return this.telemetry.getTotalIdleTimeSeconds(this.VehiclesPathLocations);
	}

	public formatSpeed(speedMph: number | null | undefined): string {
		return this.telemetry.formatSpeed(speedMph);
	}

	public formatOdometer(odometerMeters: number | null | undefined): string {
		return this.telemetry.formatOdometer(odometerMeters);
	}

	public formatDuration(totalSeconds: number | null | undefined): string {
		return this.telemetry.formatDuration(totalSeconds);
	}

	public formatIdleEventTime(epochSeconds: number): string {
		return this.telemetry.formatIdleEventTime(epochSeconds);
	}

	public async showDrawingManager(type: DrawingManagerOption) {
		this.toggleDrawer();
		this.vehicleListDrawerOpen = false
		const polygonCoords = await this.locationMapService.draw(type);
		if (type === 'Polygon') {
			this.addnewPolygon(polygonCoords as geojson.GeometryPolygon);
		} else if (type === 'Point') {
			this.addDeviceLocation(polygonCoords as geojson.GeometryPoint)
		}
	}

	public async addnewPolygon(newPolygon: geojson.GeometryPolygon) {
		await this.dialog.open(this.addSiteDialog, newPolygon);
		this.trigger$.next()
	}

	public async addDeviceLocation(newPoint: geojson.GeometryPoint) {
		await this.dialog.open(this.setDevceLocation, { point: newPoint, unassignedDevices: this.unassignedDevices });
	}

	public readonly vehicleLocations$: Observable<VehicleLocationQuery.Interval.VehicleLocationsWithPathColor[] | undefined> = combineLatest(
		[
			this.vehicleQuery.vehicleLocationForInterval$,
			this.vehicleQuery.vehicles$
		]
	).pipe(
		op.map(([response]) => response
			? this.telemetry.mapVehicleColors(response, this.vehicleProperties)
			: undefined
		)
	)

	private processVehiclesPathLocations(): void {
		this.vehicleTracePaths = [];
		const selectedVehicleIds = new Set(
			(this.vehicleProperties ?? [])
				.filter(([, , isSelected]) => isSelected)
				.map(([vehicle]) => vehicle.id)
		);
		const hasSelectionState = (this.vehicleProperties ?? []).length > 0;

		(this.VehiclesPathLocations ?? [])
			.filter((vehicleLocations) => !hasSelectionState || selectedVehicleIds.has(vehicleLocations.vehicleId))
			.forEach((vehicleLocations) => {
				this.vehicleTracePaths.push({
					content: '',
					coordinates: vehicleLocations.vehicleLocations,
					color: vehicleLocations.color.toString(),
				});
			});
	}
}

export type DrawingManagerOption = 'Polygon' | 'Point';

function isFeatureFlagEnabled(featureFlags: string | Record<string, unknown> | null | undefined, flagName: string): boolean {
	if (!featureFlags) {
		return false;
	}

	if (typeof featureFlags === 'object') {
		return featureFlags[flagName] === true;
	}

	try {
		const parsedFlags = JSON.parse(featureFlags);
		return parsedFlags?.[flagName] === true;
	} catch {
		return false;
	}
}
